import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, FileText, RefreshCw, Loader2, Wallet as WalletIcon } from "lucide-react";

export default function Wallet() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [opening, setOpening] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/wallet/summary");
            setData(r.data);
        } catch {
            setData({ has_customer: false, cards: [], invoices: [] });
        }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const openPortal = async () => {
        setOpening(true);
        try {
            const { data } = await api.post("/wallet/portal");
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open billing portal");
            setOpening(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="wallet-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><WalletIcon className="w-3.5 h-3.5" /> Wallet</p>
                    <h1 className="font-display text-4xl font-bold tracking-tight">Payment methods &amp; billing</h1>
                    <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
                        Manage saved cards, Apple Pay &amp; Google Pay, AutoPay and download your invoices — securely via Stripe.
                    </p>
                </div>
                <Button onClick={openPortal} disabled={opening} data-testid="wallet-portal-btn"
                    className="bg-espresso text-[#FAF5FF] hover:bg-black rounded-sm h-11 px-6 shrink-0">
                    {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : "Manage payment methods"}
                </Button>
            </div>

            {loading ? (
                <p className="text-sm text-zinc-500">Loading…</p>
            ) : !data?.has_customer ? (
                <div className="border border-[#DDD6FE] rounded-sm bg-paper p-10 text-center" data-testid="wallet-empty">
                    <CreditCard className="w-6 h-6 mx-auto text-zinc-400 mb-3" />
                    <h3 className="font-display text-2xl">No billing profile yet</h3>
                    <p className="text-zinc-600 mt-2 max-w-md mx-auto text-sm">
                        Once you subscribe to a plan, your saved cards, Apple/Google Pay and invoices will appear here.
                    </p>
                </div>
            ) : (
                <>
                    {/* AutoPay + cards */}
                    <div className="grid lg:grid-cols-2 gap-4">
                        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="wallet-autopay">
                            <div className="flex items-center justify-between">
                                <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">AutoPay</h3>
                                <RefreshCw className="w-4 h-4 text-zinc-400" />
                            </div>
                            <p className="font-display text-2xl mt-2">
                                {data.autopay === null ? "—" : data.autopay ? <span className="text-emerald-deep">On</span> : <span className="text-[#D97706]">Off</span>}
                            </p>
                            <p className="text-xs text-zinc-500 mt-1">Your subscription renews automatically. Toggle it in the billing portal.</p>
                        </div>
                        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="wallet-cards">
                            <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Saved cards</h3>
                            {data.cards.length === 0 ? (
                                <p className="text-sm text-zinc-500">No cards saved yet. Add one in the billing portal.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {data.cards.map((c) => (
                                        <li key={c.id} className="flex items-center gap-3 text-sm" data-testid={`wallet-card-${c.id}`}>
                                            <CreditCard className="w-4 h-4 text-zinc-500" />
                                            <span className="capitalize font-medium">{c.brand}</span>
                                            <span className="font-mono">•••• {c.last4}</span>
                                            <span className="text-zinc-500">exp {c.exp_month}/{String(c.exp_year).slice(-2)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Invoices */}
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="wallet-invoices">
                        <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Invoices</h3>
                        {data.invoices.length === 0 ? (
                            <p className="text-sm text-zinc-500">No invoices yet.</p>
                        ) : (
                            <ul className="divide-y divide-[#DDD6FE]/60">
                                {data.invoices.map((i) => (
                                    <li key={i.id} className="py-3 flex items-center justify-between gap-4" data-testid={`invoice-${i.id}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
                                            <div className="min-w-0">
                                                <p className="font-mono text-sm truncate">{i.number || i.id}</p>
                                                <p className="text-xs text-zinc-500">{new Date(i.created * 1000).toLocaleDateString()} · {i.status}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <span className="font-medium text-sm">${Number(i.amount).toFixed(2)}</span>
                                            {i.pdf && <a href={i.pdf} target="_blank" rel="noopener noreferrer" className="text-emerald-deep text-xs underline" data-testid={`invoice-pdf-${i.id}`}>PDF</a>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
